// skipcq: JS-W1028
import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';

import { db } from '../utils/firebase';

/** @type {Record<string, string> | null} */
let cachedTagLabelMap = null;
/** @type {Promise<Record<string, string>> | null} */
let tagLabelMapPromise = null;

/**
 * @returns {Promise<Record<string, string>>}
 */
async function fetchBeerReviewTagLabelMap() {
  if (cachedTagLabelMap !== null) {
    return cachedTagLabelMap;
  }

  if (tagLabelMapPromise !== null) {
    return tagLabelMapPromise;
  }

  tagLabelMapPromise = (async () => {
    const tagsRef = collection(db, 'tags');
    const tagsQuery = query(tagsRef, where('domain', '==', 'beer'));
    const snapshot = await getDocs(tagsQuery);

    const labelMap = {};
    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      const label = typeof data.label === 'string' ? data.label : '';
      if (label.length > 0) {
        labelMap[docSnap.id] = label;
      }
    });

    cachedTagLabelMap = labelMap;
    return labelMap;
  })();

  try {
    return await tagLabelMapPromise;
  } finally {
    tagLabelMapPromise = null;
  }
}

/**
 * Loads beer review tag id → label map (cached module-wide after first fetch).
 */
function useBeerReviewTags() {
  const [tagLabelMap, setTagLabelMap] = useState(cachedTagLabelMap ?? {});
  const [loading, setLoading] = useState(cachedTagLabelMap === null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadTags = async () => {
      if (cachedTagLabelMap !== null) {
        setTagLabelMap(cachedTagLabelMap);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const labelMap = await fetchBeerReviewTagLabelMap();
        if (!cancelled) {
          setTagLabelMap(labelMap);
        }
      } catch (err) {
        console.error('Error loading beer review tags:', err);
        if (!cancelled) {
          setError('Failed to load beer tags');
          setTagLabelMap({});
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTags();

    return () => {
      cancelled = true;
    };
  }, []);

  return { tagLabelMap, loading, error };
}

/** @internal Test-only cache reset */
export function resetBeerReviewTagsCacheForTests() {
  cachedTagLabelMap = null;
  tagLabelMapPromise = null;
}

export { fetchBeerReviewTagLabelMap };

export default useBeerReviewTags;
